import {
    type Asset,
    type AssetGroup,
    type DynamicAnimationConfiguration,
    type TextAssetDynamicAnimation,
    VideoData,
} from '@lumen5/luminary';

import { getTemplateBundle } from 'js/src/data/templates';
import { setAssetTimingOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { greaterThanWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { withDataLuminarySync } from 'js/src/luminary/data-luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    assetHasEntryAnimation,
    getAllAssetAnimations,
    getRootAssetGroup,
} from 'js/src/luminary/luminary-selectors';
import { saveRawLuminary } from 'js/src/luminary/utils';

export type TimingSpecification = {
    introTime: number; // absolute time in milliseconds from start of scene
    exitTime: number; // absolute time in milliseconds from start of scene
    assetId: string;
    sceneId: string;
};
type TimingSpecificationProgress = TimingSpecification & {
    complete: boolean;
};

export const setAssetIntroExitTimesOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        {
            timingSpecifications,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecifications: TimingSpecification[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        const timingSpecificationsProgress: TimingSpecificationProgress[] =
            timingSpecifications.map((specification) => {
                return {
                    ...specification,
                    complete: false,
                };
            });

        luminary.batchRun(() => {
            timingSpecificationsProgress.forEach((specification) => {
                if (specification.complete) {
                    return;
                }
                const scene = luminary.getSceneById(specification.sceneId);
                if (!scene) {
                    return;
                }

                const asset = scene.getAssetById(specification.assetId);
                if (!asset) {
                    throw new Error('Asset not found in scene');
                }

                const rootAssetGroup = getRootAssetGroup(asset);
                if (rootAssetGroup) {
                    call(_manageAssetGroupAndChildAssets, rootAssetGroup, {
                        timingSpecificationsProgress,
                        operatingOnMainLuminary,
                        systemTriggered,
                        parentActionGroup,
                    });
                } else {
                    call(_manageAsset, asset, {
                        timingSpecificationsProgress,
                        operatingOnMainLuminary,
                        systemTriggered,
                        parentActionGroup,
                    });
                }
            });
        });

        if (operatingOnMainLuminary) {
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

const _manageAssetGroupAndChildAssets = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        assetGroup: AssetGroup,
        {
            timingSpecificationsProgress,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecificationsProgress: TimingSpecificationProgress[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        assetGroup.introAnimationMode = VideoData.AT_THE_SAME_TIME_ANIMATION_MODE;
        assetGroup.outroAnimationMode = VideoData.AT_THE_SAME_TIME_ANIMATION_MODE;
        assetGroup.childIntroAnimationOffset = 0;
        assetGroup.childOutroAnimationOffset = 0;

        if (
            assetGroup.colorGraphics.length > 0 ||
            assetGroup.alphaGraphics.length > 0 ||
            assetGroup.hasObjectAnimation
        ) {
            // if the AssetGroup has color or alpha graphics, we want those to
            // enter when the first child asset enters
            // and exit when the last child asset exits
            const children = assetGroup.getAssets().filter((child) => {
                if (
                    VideoData.isColorAsset(child) ||
                    VideoData.isCircleAsset(child) ||
                    VideoData.isGradientAsset(child) ||
                    VideoData.isRectangleAsset(child) ||
                    VideoData.isCompositingAsset(child)
                ) {
                    // these assets do not have a maxHeight property, so we need to handle them separately to avoid ts errors
                    // I do not think they should ever actually be children of an asset group, but just in case...
                    return true;
                }
                // children with no content (like text assets with empty text "") get their maxHeight set to 0
                // by the asset group. For these children, we do not need to consider their entry/exit times.
                return child.maxHeight > 0;
            });
            const smallestIntroAnimationOffset =
                children
                    .map((child) => {
                        const timingSpecificationProgress = _findTimingSpecificationProgress(
                            timingSpecificationsProgress,
                            child.id,
                        );
                        if (timingSpecificationProgress) {
                            return timingSpecificationProgress.introTime;
                        }
                        return child.appliedIntroAnimationOffset;
                    })
                    .sort((a, b) => a - b)[0] ?? 0;
            const smallestOutroAnimationOffset =
                children
                    .map((child) => {
                        const timingSpecificationProgress = _findTimingSpecificationProgress(
                            timingSpecificationsProgress,
                            child.id,
                        );
                        if (timingSpecificationProgress) {
                            return (
                                child._luminaryParent.duration -
                                timingSpecificationProgress.exitTime
                            );
                        }
                        return child.appliedOutroAnimationOffset;
                    })
                    .sort((a, b) => a - b)[0] ?? 0;
            _minimizeAnimationDelays(assetGroup);
            assetGroup.introAnimationOffset = smallestIntroAnimationOffset;
            assetGroup.outroAnimationOffset = smallestOutroAnimationOffset;
            assetGroup.animateOutBehaviour = call(_calculateAnimateOutBehaviour, assetGroup);
        }
        assetGroup.getAssets({ deep: false }).forEach((asset) => {
            if (VideoData.isAssetGroup(asset)) {
                call(_manageAssetGroupAndChildAssets, asset, {
                    timingSpecificationsProgress,
                    operatingOnMainLuminary,
                    systemTriggered,
                    parentActionGroup,
                });
                return;
            }
            call(_manageAsset, asset, {
                timingSpecificationsProgress,
                operatingOnMainLuminary,
                systemTriggered,
                parentActionGroup,
            });
        });
    },
);

const _findTimingSpecificationProgress = (
    timingSpecificationsProgress: TimingSpecificationProgress[],
    assetId: string,
) => {
    return timingSpecificationsProgress.find((specification) => specification.assetId === assetId);
};

const _manageAsset = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        asset: Asset,
        {
            timingSpecificationsProgress,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecificationsProgress: TimingSpecificationProgress[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        const hasEntryAnimation = assetHasEntryAnimation(asset);
        if (!hasEntryAnimation) {
            if (VideoData.isMediaAsset(asset) || VideoData.isIconAsset(asset)) {
                const existingAlphaAnimation = asset.objectAnimation.find(
                    (animation) => animation.property === 'alpha',
                );
                const animation = existingAlphaAnimation || asset.addObjectAnimation();
                _setupAlphaAnimation(animation);
            }
            if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
                const existingNormalStyleAlphaAnimation =
                    asset.normalStyle.animationConfigurations.find(
                        (animation) => animation.property === 'alpha',
                    );
                const normalStyleAlphaAnimation =
                    existingNormalStyleAlphaAnimation || asset.normalStyle.addAnimation();
                _setupAlphaAnimation(normalStyleAlphaAnimation);
            }
            if (VideoData.isTextAsset(asset)) {
                const existingHighlightStyleAlphaAnimation =
                    asset.highlightStyle.animationConfigurations.find(
                        (animation) => animation.property === 'alpha',
                    );
                const highlightedStyleAlphaAnimation =
                    existingHighlightStyleAlphaAnimation || asset.highlightStyle.addAnimation();
                _setupAlphaAnimation(highlightedStyleAlphaAnimation);

                for (const graphic of asset.highlightStyle.graphics) {
                    const existingGraphicAlphaAnimation = graphic.animationConfigurations.find(
                        (animation) => animation.property === 'alpha',
                    );
                    const graphicAlphaAnimation =
                        existingGraphicAlphaAnimation || graphic.addAnimation();
                    _setupAlphaAnimation(graphicAlphaAnimation);
                }
            }
        }

        const timingSpecificationProgress = _findTimingSpecificationProgress(
            timingSpecificationsProgress,
            asset.id,
        );
        if (timingSpecificationProgress) {
            const outroAnimationOffset =
                asset._luminaryParent.duration - timingSpecificationProgress.exitTime;
            _minimizeAnimationDelays(asset);
            asset.introAnimationOffset = timingSpecificationProgress.introTime;
            asset.outroAnimationOffset = outroAnimationOffset;
            asset.animateOutBehaviour = call(_calculateAnimateOutBehaviour, asset);
        } else {
            _minimizeAnimationDelays(asset);
            asset.introAnimationOffset = asset.appliedIntroAnimationOffset;
            asset.outroAnimationOffset = asset.appliedOutroAnimationOffset;
        }
        if (operatingOnMainLuminary) {
            dispatch(
                setAssetTimingOverride({
                    sceneId: asset.slide.id,
                    assetId: asset.id,
                    isOverridden: true,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);
const _minimizeAnimationDelays = (asset: Asset) => {
    // each animation configuration can have a delay. If all the animations
    // have a delay built into them, it is effectively increasing the introAnimationOffset
    // for the entire asset. This throws off the calculations for the timing:
    // asset.appliedIntroAnimationOffset won't equal asset.introAnimationOffset!
    // To get around this, we make it so that the delay of each animation is shortened
    // so that at least one animation has a delay of 0.
    const allAnimations = getAllAssetAnimations(asset);
    let minAnimationDelay = Infinity;
    for (let i = 0; i < allAnimations.length; i++) {
        if (allAnimations[i].delay < minAnimationDelay) {
            minAnimationDelay = allAnimations[i].delay;
        }
    }
    if (minAnimationDelay < Infinity) {
        allAnimations.forEach((animation) => {
            animation.delay -= minAnimationDelay;
        });
    }
};
const _setupAlphaAnimation = (
    animation: DynamicAnimationConfiguration | TextAssetDynamicAnimation,
) => {
    animation.property = 'alpha';
    animation.duration = 10;
    animation.startValue = 0;
    animation.endValue = 1;
};

const _calculateAnimateOutBehaviour = createLuminaryOperation(
    ({ luminary, getState }, asset: Asset) => {
        const noForcedOutroAnimation = VideoData.isSubscene(asset._luminaryParent)
            ? VideoData.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
            : VideoData.ANIMATE_OUT_BEHAVIOUR_NEVER;

        if (greaterThanWithinEpsilon(asset.outroAnimationOffset, 0)) {
            // if the asset is going to exit before the end of the container, we need to animate it out
            return VideoData.ANIMATE_OUT_BEHAVIOUR_ALWAYS;
        }

        // if the outro animation offset is 0, we need to still decide whether or not to animate out the asset
        // use the scene design to determine the right answer, defaulting to never.
        const templateBundle = getTemplateBundle(getState(), luminary.template.id);
        if (!templateBundle) {
            return noForcedOutroAnimation;
        }

        return withDataLuminarySync(
            (dataLuminary) => {
                const { scene: dataScene } = dataLuminary.addScene({
                    sceneDesignId: asset.slide.sceneDesign,
                });
                const dataAsset = dataScene.getAssetByName(asset.name);
                if (dataAsset) {
                    return dataAsset.animateOutBehaviour;
                }
                return noForcedOutroAnimation;
            },
            {
                templateBundle,
            },
        );
    },
);
